import { createContext, useContext, useState, useCallback, useMemo, type ReactNode, type ComponentType } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, TextField, Select, MenuItem, FormControlLabel, Switch, Box, InputLabel, FormControl } from '@mui/material';
import { dialogConfig } from '../config/ui/dialogConfig';

interface DialogInputConfig {
	name: string;
	label: string;
	type: 'text' | 'select' | 'switch' | 'date';
	required?: boolean;
	multiline?: boolean;
	rows?: number;
	defaultValue?: unknown;
	options?: Array<{ value: string; label: string }>;
	condition?: ((data: Record<string, unknown>) => boolean) | boolean;
}

interface DialogAction {
	label: string;
	value: unknown;
	color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | 'inherit';
}

interface DialogConfigEntry {
	title?: string;
	message?: string;
	actions?: DialogAction[];
	inputs?: DialogInputConfig[];
	actionLabel?: string;
	component?: ComponentType<Record<string, unknown>>;
}

interface DialogState {
	config: DialogConfigEntry;
	callback?: ((value: unknown) => void) | null;
	data: Record<string, unknown> & {
		inputs?: DialogInputConfig[];
		component?: ComponentType<Record<string, unknown>>;
		title?: string;
		maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
	};
	messageOverride?: string;
}

interface ShowDialogParams {
	id: string;
	callback?: ((value: unknown) => void) | null;
	data?: Record<string, unknown>;
	messageOverride?: string;
}

export interface DialogContextValue {
	showDialog: (params: ShowDialogParams) => void;
}

const DialogContext = createContext<DialogContextValue | undefined>(undefined);

export const DialogProvider = ({ children }: { children: ReactNode }) => {
	const [dialog, setDialog] = useState<DialogState | null>(null);
	const [formData, setFormData] = useState<Record<string, unknown>>({});

	const handleClose = () => {
		setDialog(null);
		setFormData({});
	};

	const showDialog = useCallback(({ id, callback, data, messageOverride }: ShowDialogParams) => {
		const dialogSettings = dialogConfig[id] as DialogConfigEntry | undefined;
		if (!dialogSettings) {
			console.error(`Dialog with id '${id}' not found in dialogConfig.`);
			return;
		}

		const inputs = (data?.inputs as DialogInputConfig[] | undefined) || dialogSettings.inputs;

		if (inputs) {
			const initialFormData = inputs.reduce<Record<string, unknown>>((acc, input) => {
				acc[input.name] = (data as Record<string, unknown>)?.[input.name] ?? input.defaultValue ?? (input.type === 'switch' ? false : '');
				return acc;
			}, {});
			setFormData(initialFormData);
		}

		setDialog({
			config: dialogSettings,
			callback,
			data: { ...data, inputs, component: (data?.component || dialogSettings.component) as ComponentType<Record<string, unknown>> | undefined },
			messageOverride,
		});
	}, []);

	const handleActionClick = (value: unknown) => {
		if (dialog?.callback) {
			dialog.callback(value);
		}
		handleClose();
	};

	const handleFormSubmit = () => {
		if (dialog?.callback) {
			dialog.callback(formData);
		}
		handleClose();
	};

	const handleInputChange = (event: { target: { name: string; value: unknown; type?: string; checked?: boolean } }) => {
		const { name, value, type, checked } = event.target;
		setFormData((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value,
		}));
	};

	const renderInputs = () => {
		const inputsToRender = dialog?.data?.inputs || dialog?.config?.inputs;
		if (!inputsToRender) return null;

		return inputsToRender.map((inputConfig) => {
			if (inputConfig.condition) {
				if (typeof inputConfig.condition === 'function' && !inputConfig.condition(dialog!.data)) {
					return null;
				}
			}

			switch (inputConfig.type) {
				case 'text':
					return <TextField key={inputConfig.name} name={inputConfig.name} label={inputConfig.label} value={(formData[inputConfig.name] as string) || ''} onChange={handleInputChange} fullWidth margin='dense' variant='outlined' multiline={!!inputConfig.multiline} rows={inputConfig.rows} />;
				case 'select':
					return (
						<FormControl key={inputConfig.name} fullWidth margin='dense'>
							<InputLabel>{inputConfig.label}</InputLabel>
							<Select name={inputConfig.name} value={(formData[inputConfig.name] as string) || ''} label={inputConfig.label} onChange={handleInputChange as never}>
								{inputConfig.options?.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					);
			case 'date':
				return <TextField key={inputConfig.name} name={inputConfig.name} label={inputConfig.label} type='datetime-local' value={(formData[inputConfig.name] as string) || ''} onChange={handleInputChange} fullWidth margin='dense' variant='outlined' slotProps={{ inputLabel: { shrink: true } }} />;
			case 'switch':
				return <FormControlLabel key={inputConfig.name} control={<Switch name={inputConfig.name} checked={!!formData[inputConfig.name]} onChange={handleInputChange} />} label={inputConfig.label} />;
			default:
					return null;
			}
		});
	};

	const renderActions = () => {
		if (!dialog) return null;

		const inputsToRender = dialog.data?.inputs || dialog.config?.inputs;
		if (inputsToRender) {
			return (
				<>
					<Button onClick={() => handleActionClick(null)}>Cancel</Button>
					<Button onClick={handleFormSubmit} variant='contained'>
						{dialog.config.actionLabel || 'Submit'}
					</Button>
				</>
			);
		}

		if (dialog.config.actions) {
			return (
				<>
					<Button onClick={() => handleActionClick(null)}>Cancel</Button>
					{dialog.config.actions.map((action) => (
						<Button key={action.label} onClick={() => handleActionClick(action.value)} color={action.color || 'primary'}>
							{action.label}
						</Button>
					))}
				</>
			);
		}

		return (
			<>
				<Button onClick={() => handleActionClick(null)}>Cancel</Button>
				<Button onClick={() => handleActionClick(true)} autoFocus>
					Confirm
				</Button>
			</>
		);
	};

	const contextValue = useMemo<DialogContextValue>(() => ({ showDialog }), [showDialog]);

	return (
		<DialogContext.Provider value={contextValue}>
			{children}
			{dialog && (
				<Dialog open={true} onClose={() => handleActionClick(null)} fullWidth maxWidth={dialog.data?.maxWidth || 'sm'}>
					{dialog.data?.component ? (
						(() => {
							const Component = dialog.data.component;
							return <Component onSuccess={() => handleActionClick(true)} onClose={() => handleActionClick(null)} onCancel={() => handleActionClick(null)} {...dialog.data} />;
						})()
					) : (
						<>
							<DialogTitle>{dialog.data?.title || dialog.config.title}</DialogTitle>
							<DialogContent>
								<DialogContentText sx={{ mb: 2, color: 'text.active' }}>{dialog.messageOverride || dialog.config.message}</DialogContentText>
								<Box component='form' noValidate autoComplete='off'>
									{renderInputs()}
								</Box>
							</DialogContent>
							<DialogActions>{renderActions()}</DialogActions>
						</>
					)}
				</Dialog>
			)}
		</DialogContext.Provider>
	);
};

export const useDialog = (): DialogContextValue => {
	const context = useContext(DialogContext);
	if (context === undefined) {
		throw new Error('useDialog must be used within a DialogProvider');
	}
	return context;
};
